/**
 * Stage-move guards (docs/proposal-v2/S10.md §3.3) — pure, no Firebase, so
 * both stage writers share one rule: `moveDealToStage` /
 * `revertDealStage` in `deal-workflow.service.ts` (the chokepoint, which
 * re-exports these) and the legacy `moveDealToStage` twin in
 * `deal.service.ts` (the quote-review approve path).
 */

import { DealStage, type Deal } from '@/types/theo-workflow.types';
import type { DealAcceptanceSelectionSummary } from '@/types/proposal.types';

/**
 * A stage move the deal may not make. The message is written for the
 * office — callers show it the way they show any other error.
 */
export class DealStageBlockedError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'DealStageBlockedError';
  }
}

/** Is `err` a refused stage move? (By name, so it holds across a dynamic import of the service.) */
export function isDealStageBlockedError(err: unknown): err is DealStageBlockedError {
  return err instanceof Error && err.name === 'DealStageBlockedError';
}

export type GuardedDeal = Pick<Deal, 'proposalBuilderVersion' | 'proposalV2'> & {
  acceptanceSelectionSummary?: Pick<DealAcceptanceSelectionSummary, 'acceptedOptionId'> | null;
};

/**
 * The manual-WON guard: a deal on the new proposal builder that offers the
 * customer a choice (two or more options) may not be moved to WON by hand
 * until the office locks one option ("Lock option…" in the builder sets
 * `proposalV2.lockedOptionId`) or the customer accepted one
 * (`acceptanceSelectionSummary.acceptedOptionId` — the acceptance trigger
 * moves the deal to WON server-side and never comes through here).
 * Otherwise nobody downstream knows which option was sold. Returns the
 * office-facing reason, or null when the move may go ahead.
 */
export function dealStageBlockedReason(deal: GuardedDeal | null | undefined, stage: DealStage): string | null {
  if (stage !== DealStage.WON || !deal || deal.proposalBuilderVersion !== 2) return null;
  if ((deal.proposalV2?.options?.length ?? 0) < 2) return null;
  if (deal.proposalV2?.lockedOptionId || deal.acceptanceSelectionSummary?.acceptedOptionId) return null;
  return 'Pick an option before marking this deal won';
}

/** Throw `DealStageBlockedError` when `deal` may not enter `stage`. */
export function assertDealMayEnterStage(deal: GuardedDeal | null | undefined, stage: DealStage): void {
  const reason = dealStageBlockedReason(deal, stage);
  if (reason) throw new DealStageBlockedError(reason);
}
